import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  InputBase,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import Picker from "emoji-picker-react";
import { AppColors } from "../../../../../util/AppColors";

const toolBtnSx = {
  color: "#64748B",
  p: 0.75,
  borderRadius: "8px",
  "&:hover": {
    color: "#0054A6",
    backgroundColor: "#F1F5F9",
  },
  "& svg": { fontSize: "20px" },
};

const ChatComposer = ({ onSendMessage }) => {
  const [msgText, setMsgText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const pressEnterToSend = true;

  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const canSend = msgText.trim() !== "" || attachedFiles.length > 0;

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (cameraOpen) {
      const getStream = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: false,
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          streamRef.current = stream;
        } catch (err) {
          console.error("Camera access error:", err);
          alert("Unable to access camera. Please check permissions.");
          setCameraOpen(false);
        }
      };
      getStream();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [cameraOpen]);

  const captureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth || 640;
      canvas.height = videoRef.current.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File(
              [blob],
              `camera_capture_${Date.now()}.jpg`,
              { type: "image/jpeg" }
            );
            setAttachedFiles((prev) => [...prev, file]);
          }
        },
        "image/jpeg",
        0.95
      );

      setCameraOpen(false);
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    if (onSendMessage) {
      onSendMessage(msgText, attachedFiles);
    }
    setMsgText("");
    setAttachedFiles([]);
    setEmojiPickerVisible(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && pressEnterToSend) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setMsgText((prev) => prev + emojiData.emoji);
    setEmojiPickerVisible(false);
  };

  const handleActionClick = (type) => {
    if (type === "Photo") {
      setCameraOpen(true);
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.accept = "*/*";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setAttachedFiles((prev) => [...prev, ...filesArray]);
      e.target.value = null;
    }
  };

  const removeAttachedFile = (idxToRemove) => {
    setAttachedFiles((prev) => prev.filter((_, idx) => idx !== idxToRemove));
  };

  return (
    <Box
      sx={{
        px: 1,
        pt: 0.75,
        pb: 1,
        backgroundColor: AppColors.white,
        borderTop: "none",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        multiple
      />

      {attachedFiles.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 1.5,
            p: 1,
            width: "100%",
            backgroundColor: AppColors.white,
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
          }}
        >
          {attachedFiles.map((file, idx) => {
            const isImage = file.type.startsWith("image/");
            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  backgroundColor: AppColors.white,
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  p: 0.75,
                  pr: 1,
                  maxWidth: "200px",
                }}
              >
                {isImage ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "4px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <DescriptionIcon sx={{ color: "#EF4444", fontSize: "20px" }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "11px",
                      fontWeight: "600",
                      fontFamily: "Montserrat",
                      color: "#1E293B",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontFamily: "Montserrat",
                      color: "#64748B",
                    }}
                  >
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removeAttachedFile(idx)}
                  sx={{
                    p: 0.25,
                    color: "#94A3B8",
                    "&:hover": { color: "#EF4444" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Montserrat",
            fontWeight: "700",
            fontSize: "16px",
            pb: 1,
          }}
        >
          Capture Photo
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#000",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "320px",
          }}
        >
          {cameraOpen && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "400px",
                objectFit: "cover",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => setCameraOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontFamily: "Montserrat",
              fontWeight: "600",
              borderRadius: "8px",
              borderColor: "#E2E8F0",
              color: "#64748B",
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F8FAFC",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={captureImage}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "Montserrat",
              fontWeight: "600",
              borderRadius: "8px",
              backgroundColor: "#0054A6",
              "&:hover": { backgroundColor: "#004080" },
            }}
          >
            Capture Image
          </Button>
        </DialogActions>
      </Dialog>

      {emojiPickerVisible && (
        <Box
          sx={{
            position: "absolute",
            bottom: "72px",
            right: 24,
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Picker onEmojiClick={handleEmojiClick} width={320} height={350} />
        </Box>
      )}

      {/* Full-width white input bar */}
      <Paper
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: 2,
          pr: 0.75,
          py: 0.5,
          minHeight: 48,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          backgroundColor: AppColors.white,
          borderRadius: "24px",
          border: "1px solid #CBD5E1",
          boxShadow: "none",
        }}
      >
        <InputBase
          value={msgText}
          onChange={(e) => setMsgText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "14px",
            fontFamily: "Montserrat",
            color: "#1E293B",
            "& input::placeholder": {
              fontFamily: "Montserrat",
              color: "#94A3B8",
              opacity: 1,
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => setEmojiPickerVisible((v) => !v)}
            aria-label="Emoji"
            sx={toolBtnSx}
          >
            <InsertEmoticonIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleActionClick("Attach")}
            aria-label="Attach"
            sx={toolBtnSx}
          >
            <AttachFileIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleActionClick("Photo")}
            aria-label="Photo"
            sx={toolBtnSx}
          >
            <CameraAltIcon />
          </IconButton>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.75, my: 1, borderColor: "#E2E8F0" }}
          />

          <IconButton
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send"
            sx={{
              color: canSend ? "#0054A6" : "#94A3B8",
              p: 0.75,
              borderRadius: "8px",
              "&:hover": {
                color: "#004080",
                backgroundColor: canSend ? "#E2F0FD" : "transparent",
              },
              "&.Mui-disabled": {
                color: "#CBD5E1",
              },
              "& svg": { fontSize: "20px" },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChatComposer;
